import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styles from './VNCartButtons.module.scss'
import VNPlusButton from './VNPlusButton'
import { useDispatch, useSelector } from 'react-redux'
import useOfflineModal from '../../hooks/useOfflineModal'
import { setRoute } from '../../VNRoutes/ActionCreators'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'

import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { getCartNotes } from '../../selectors/cart'
import { getIsLocationPickerActive } from '../../VNLocationPicker/Selectors'
import CartItem from '../../components/CartItem'
import { addNotesToCart } from '../../actions/cart'
import { VNDialog } from '../../VNCustomerFacingDisplay/components/VNDialog'
import useGetSeats from '../../hooks/useGetSeats'
import DropdownSelector from '../../components/DropdownSelector'

const VNLocation = () => {
  const dispatch = useDispatch()
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [section, setSection] = useState('')
  const [isSuite, setIsSuite] = useState(false)
  const [row, setRow] = useState('')
  const [seat, setSeat] = useState('')
  const networkAvailable = getNetworkAvailableValue()
  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const isMobileViewPort = useGetIsMobileScreen()
  const cartNotes = useSelector(getCartNotes)
  const isLocationPickerActive = useSelector(getIsLocationPickerActive)
  const [getRows, getSeats, { sections, rows, seats, seatsLoading }] = useGetSeats()
  const localSections = useMemo(() => sections?.map((section) => section.name), [sections])

  const locationSubtext = isMobileViewPort
    ? 'Location selection is not supported while the device is offline.'
    : 'Location selection is not supported\nwhile the device is offline.'

  useEffect(() => {
    if (seatsLoading || !section || !sections || sections.length === 0) {
      return
    }

    const suite = sections.find((storedSection) => storedSection.name === section)?.isSuite

    setIsSuite(suite)
  }, [section, sections, seatsLoading])

  useEffect(() => {
    if (seatsLoading || !row || !rows || rows.length === 0) {
      return
    }

    getSeats(section, row)
  }, [row, getSeats, section, rows, seatsLoading])

  const locationHandler = useCallback(() => {
    checkIfOfflineWithCallback(
      () => {
        if (isMobileViewPort) {
          dispatch(setRoute('/concession-order/select-location'))
          return
        }
        setShowLocationModal(true)
      },
      locationSubtext,
      false,
    )
  }, [dispatch, checkIfOfflineWithCallback, locationSubtext, isMobileViewPort])

  const removeHandler = useCallback(() => dispatch(addNotesToCart(null)), [dispatch])

  const doneHandler = useCallback(() => {
    if (isSuite && !section) {
      return
    } else if (!isSuite && (!section || !row || !seat)) {
      return
    }

    if (isSuite) {
      dispatch(addNotesToCart(`Suite ${section}`))
    } else {
      dispatch(addNotesToCart(`Section ${section},\nRow ${row},\nSeat ${seat}`))
    }
    setRow('')
    setSeat('')
    setSection('')
    setIsSuite(false)
    setShowLocationModal(false)
  }, [dispatch, isSuite, section, row, seat])

  const cancelHandler = useCallback(() => {
    setRow('')
    setSeat('')
    setSection('')
    setIsSuite(false)
    setShowLocationModal(false)
  }, [])

  const seatSelectorContent = (
    <div style={{ paddingRight: 8, paddingLeft: 16 }}>
      <DropdownSelector
        autocompleteLabel={'Section'}
        autocompleteOptions={localSections}
        onAutocompleteChange={(inputValue) => {
          setSection(inputValue)
          setRow('')
          setSeat('')
          getRows(inputValue)
        }}
        textInputVariant={'filled'}
        disabled={seatsLoading}
      />
      {!isSuite && (
        <>
          <DropdownSelector
            autocompleteLabel={'Row'}
            textInputVariant={'filled'}
            autocompleteOptions={rows}
            onAutocompleteChange={(inputValue) => {
              setRow(inputValue)
            }}
            disabled={isSuite || seatsLoading || !section}
          />
          <DropdownSelector
            autocompleteLabel={'Seat'}
            textInputVariant={'filled'}
            autocompleteOptions={seats}
            onAutocompleteChange={(inputValue) => {
              setSeat(inputValue)
            }}
            disabled={isSuite || seatsLoading || !row}
          />
        </>
      )}
    </div>
  )

  if (!isLocationPickerActive) {
    return null
  }

  return (
    <>
      {networkAvailable && (
        <VNDialog
          open={showLocationModal}
          subTitle={'Location'}
          label={'Location'}
          showTextfield={false}
          height={isSuite ? '36%' : '55%'}
          width={'32.5%'}
          primaryButtonText={'DONE'}
          customContent={seatSelectorContent}
          primaryButtonDisabled={isSuite ? !section : !section || !seat || !row}
          onPrimaryButtonClick={doneHandler}
          secondaryButtonText={'CANCEL'}
          onSecondaryButtonClick={cancelHandler}
          fullWidth
        />
      )}
      {cartNotes && (
        <CartItem
          key={`${cartNotes}`}
          onRemove={removeHandler}
          className={styles.cartItem}
          isSpecialtyItem={true}
          onSpecialtyItemClick={() => {
            if (isMobileViewPort) {
              dispatch(setRoute('/concession-order/select-location'))
            } else {
              setShowLocationModal(true)
            }
          }}
          name={`Location: ${cartNotes}`}
        />
      )}
      {!cartNotes && (
        <div className={styles.container}>
          {offlineModal}
          <span className={styles.title}>Location</span>
          <VNPlusButton className={styles.button} onClick={locationHandler} />
        </div>
      )}
    </>
  )
}

export default memo(VNLocation)
